import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, FolderOpen, Bookmark, TrendingUp, TrendingDown,
  Minus, Zap, BarChart2, ChevronRight, Clock,
} from 'lucide-react';
import { EmptyState } from '../../components/ui';
import { cn } from '../../lib/utils';
import { ROUTES } from '../../routes/constants';
import { optionsApi } from '../../api/client';

// ─── Template Data ────────────────────────────────────────────────────────────

const STRATEGY_TEMPLATES = [
  {
    id: 'bull-call-spread',
    name: 'Bull Call Spread',
    description: 'Buy lower strike CE, sell higher strike CE. Capped profit on upside.',
    family: 'bullish',
    tags: ['bullish', 'debit', 'limited risk'],
  },
  {
    id: 'bull-put-spread',
    name: 'Bull Put Spread',
    description: 'Sell higher strike PE, buy lower strike PE. Collect premium on upside.',
    family: 'bullish',
    tags: ['bullish', 'credit', 'limited risk'],
  },
  {
    id: 'bear-call-spread',
    name: 'Bear Call Spread',
    description: 'Sell lower strike CE, buy higher strike CE. Premium income on downside.',
    family: 'bearish',
    tags: ['bearish', 'credit', 'limited risk'],
  },
  {
    id: 'bear-put-spread',
    name: 'Bear Put Spread',
    description: 'Buy higher strike PE, sell lower strike PE. Defined profit on decline.',
    family: 'bearish',
    tags: ['bearish', 'debit', 'limited risk'],
  },
  {
    id: 'long-straddle',
    name: 'Long Straddle',
    description: 'Buy ATM CE and PE. Profit from large moves in either direction.',
    family: 'volatility',
    tags: ['neutral', 'debit', 'unlimited profit'],
  },
  {
    id: 'short-straddle',
    name: 'Short Straddle',
    description: 'Sell ATM CE and PE. Collect premium when market stays range-bound.',
    family: 'volatility',
    tags: ['neutral', 'credit', 'limited profit'],
  },
  {
    id: 'long-strangle',
    name: 'Long Strangle',
    description: 'Buy OTM CE and PE. Lower cost than straddle, needs bigger move.',
    family: 'volatility',
    tags: ['neutral', 'debit', 'unlimited profit'],
  },
  {
    id: 'short-strangle',
    name: 'Short Strangle',
    description: 'Sell OTM CE and PE. Wider breakeven than straddle with limited profit.',
    family: 'volatility',
    tags: ['neutral', 'credit', 'limited profit'],
  },
  {
    id: 'iron-condor',
    name: 'Iron Condor',
    description: 'Combine bull put and bear call spreads. Ideal for low-volatility markets.',
    family: 'neutral',
    tags: ['neutral', 'credit', 'limited risk'],
  },
  {
    id: 'iron-fly',
    name: 'Iron Fly',
    description: 'Short straddle with wings for protection. High premium, capped risk.',
    family: 'neutral',
    tags: ['neutral', 'credit', 'limited risk'],
  },
  {
    id: 'butterfly',
    name: 'Butterfly',
    description: 'Three-strike strategy. Maximum profit if underlying pins center strike.',
    family: 'neutral',
    tags: ['neutral', 'debit', 'limited risk'],
  },
  {
    id: 'calendar',
    name: 'Calendar Spread',
    description: 'Same strike, different expiry. Profits from time decay differential.',
    family: 'neutral',
    tags: ['neutral', 'debit', 'time decay'],
  },
  {
    id: 'jade-lizard',
    name: 'Jade Lizard',
    description: 'Short PE + bear call spread. No upside risk with premium income.',
    family: 'bullish',
    tags: ['bullish', 'credit', 'no upside risk'],
  },
  {
    id: 'custom-multi-leg',
    name: 'Custom Multi-Leg',
    description: 'Start from scratch. Build any strategy with full leg-by-leg control.',
    family: 'custom',
    tags: ['custom', 'manual'],
  },
];

const FAMILY_FILTERS = [
  { id: 'all',        label: 'All' },
  { id: 'bullish',    label: 'Bullish' },
  { id: 'bearish',    label: 'Bearish' },
  { id: 'neutral',    label: 'Neutral' },
  { id: 'volatility', label: 'Volatility' },
  { id: 'custom',     label: 'Custom' },
];

// ─── Tag styling ──────────────────────────────────────────────────────────────

function tagStyle(tag) {
  if (tag === 'bullish')            return 'bg-bull/10 text-bull border-bull/20';
  if (tag === 'bearish')            return 'bg-bear/10 text-bear border-bear/20';
  if (tag === 'neutral')            return 'bg-info/10 text-info border-info/20';
  if (tag === 'credit')             return 'bg-amber/10 text-amber border-amber/20';
  if (tag === 'debit')              return 'bg-bg-elevated text-txt-secondary border-bg-border';
  if (tag === 'limited risk')       return 'bg-bg-elevated text-txt-muted border-bg-border';
  if (tag === 'unlimited profit')   return 'bg-bull/10 text-bull border-bull/20';
  if (tag === 'limited profit')     return 'bg-warn/10 text-warn border-warn/25';
  if (tag === 'time decay')         return 'bg-info/10 text-info border-info/20';
  if (tag === 'no upside risk')     return 'bg-bull/10 text-bull border-bull/20';
  if (tag === 'custom')             return 'bg-amber/10 text-amber border-amber/20';
  if (tag === 'manual')             return 'bg-bg-elevated text-txt-muted border-bg-border';
  return 'bg-bg-elevated text-txt-muted border-bg-border';
}

function familyIcon(family) {
  if (family === 'bullish')    return <TrendingUp size={13} className="text-bull" />;
  if (family === 'bearish')    return <TrendingDown size={13} className="text-bear" />;
  if (family === 'volatility') return <Zap size={13} className="text-amber" />;
  if (family === 'neutral')    return <Minus size={13} className="text-info" />;
  return <BarChart2 size={13} className="text-txt-muted" />;
}

// ─── Payoff Thumbnail ─────────────────────────────────────────────────────────

function PayoffThumbnail({ family }) {
  const gradients = {
    bullish:    'from-bull/5 via-bull/10 to-transparent',
    bearish:    'from-bear/5 via-bear/10 to-transparent',
    neutral:    'from-info/5 via-info/8 to-transparent',
    volatility: 'from-amber/5 via-amber/10 to-transparent',
    custom:     'from-amber/5 via-bg-elevated to-transparent',
  };
  const lineColors = {
    bullish:    'bg-bull/40',
    bearish:    'bg-bear/40',
    neutral:    'bg-info/40',
    volatility: 'bg-amber/40',
    custom:     'bg-txt-muted/30',
  };
  const grad = gradients[family] || gradients.custom;
  const line = lineColors[family] || lineColors.custom;

  return (
    <div className={cn(
      'w-full h-[68px] rounded-md border border-bg-border/60 bg-gradient-to-br overflow-hidden relative',
      grad
    )}>
      {/* Simplified payoff line shapes per family */}
      {family === 'bullish' && (
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 120 68" preserveAspectRatio="none">
          <polyline points="0,56 40,56 80,16 120,16" fill="none" stroke="rgba(16,185,129,0.5)" strokeWidth="1.5" />
          <polyline points="0,56 40,56 80,16 120,16" fill="rgba(16,185,129,0.06)" />
        </svg>
      )}
      {family === 'bearish' && (
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 120 68" preserveAspectRatio="none">
          <polyline points="0,16 40,16 80,56 120,56" fill="none" stroke="rgba(239,68,68,0.5)" strokeWidth="1.5" />
        </svg>
      )}
      {family === 'neutral' && (
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 120 68" preserveAspectRatio="none">
          <polyline points="0,52 25,52 60,18 95,52 120,52" fill="none" stroke="rgba(59,130,246,0.5)" strokeWidth="1.5" />
        </svg>
      )}
      {family === 'volatility' && (
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 120 68" preserveAspectRatio="none">
          <polyline points="0,12 50,52 70,52 120,12" fill="none" stroke="rgba(245,158,11,0.5)" strokeWidth="1.5" />
        </svg>
      )}
      {family === 'custom' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <BarChart2 size={20} className="text-txt-muted/30" />
        </div>
      )}
      {/* Baseline */}
      <div className={cn('absolute bottom-[22px] left-0 right-0 h-px', line)} />
    </div>
  );
}

// ─── Strategy Card ────────────────────────────────────────────────────────────

function StrategyCard({ template, onUse }) {
  return (
    <div className="panel p-4 flex flex-col gap-3 hover:border-bg-hover transition-colors group">
      <PayoffThumbnail family={template.family} />

      <div className="flex items-start gap-2">
        <div className="mt-0.5 shrink-0">{familyIcon(template.family)}</div>
        <div className="min-w-0">
          <div className="font-display font-semibold text-sm text-txt-primary leading-snug group-hover:text-amber transition-colors">
            {template.name}
          </div>
          <p className="text-xs text-txt-secondary font-mono mt-0.5 leading-relaxed">
            {template.description}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {template.tags.map((tag) => (
          <span
            key={tag}
            className={cn('badge border text-2xs font-mono', tagStyle(tag))}
          >
            {tag}
          </span>
        ))}
      </div>

      <button
        onClick={() => onUse(template)}
        className="btn-secondary text-xs w-full mt-auto flex items-center justify-center gap-1.5"
      >
        Use Template
        <ChevronRight size={12} />
      </button>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function OptionsHomePage() {
  const navigate = useNavigate();
  const [activeFamily, setActiveFamily] = useState('all');
  const [strategies, setStrategies] = useState([]);
  const [strategiesLoading, setStrategiesLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;
    setStrategiesLoading(true);
    optionsApi.list()
      .then((rows) => { if (!isCancelled) setStrategies(rows); })
      .catch(() => { if (!isCancelled) setStrategies([]); })
      .finally(() => { if (!isCancelled) setStrategiesLoading(false); });
    return () => { isCancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (activeFamily === 'all') return STRATEGY_TEMPLATES;
    return STRATEGY_TEMPLATES.filter((t) => t.family === activeFamily);
  }, [activeFamily]);

  function handleUseTemplate(template) {
    navigate(ROUTES.OPTIONS_NEW, { state: { template } });
  }

  function handleNewStrategy() {
    navigate(ROUTES.OPTIONS_NEW);
  }

  return (
    <div className="flex flex-col gap-6 max-w-[1280px]">

      {/* ── Hero Header ── */}
      <div className="panel p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 mb-1.5">
            <div className="w-7 h-7 rounded-lg bg-amber/10 border border-amber/20 flex items-center justify-center shrink-0">
              <span className="font-display font-bold text-amber text-xs">O</span>
            </div>
            <h1 className="font-display font-bold text-xl text-txt-primary tracking-wide">
              O For Option
            </h1>
          </div>
          <p className="text-sm text-txt-muted font-mono">
            Build, analyze, and automate multi-leg option strategies
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => {}}
            className="btn-ghost text-xs flex items-center gap-1.5"
          >
            <Bookmark size={13} />
            Saved
          </button>
          <button
            onClick={() => {}}
            className="btn-secondary text-xs flex items-center gap-1.5"
          >
            <FolderOpen size={13} />
            Open Draft
          </button>
          <button
            onClick={handleNewStrategy}
            className="btn-primary text-xs flex items-center gap-1.5"
          >
            <Plus size={13} />
            New Strategy
          </button>
        </div>
      </div>

      {/* ── Templates Section ── */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display font-semibold text-sm text-txt-primary tracking-wide">
              Strategy Templates
            </h2>
            <p className="text-xs text-txt-muted font-mono mt-0.5">
              {filtered.length} template{filtered.length !== 1 ? 's' : ''}
              {activeFamily !== 'all' ? ` · ${activeFamily}` : ''}
            </p>
          </div>
        </div>

        {/* Family Filter Chips */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {FAMILY_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setActiveFamily(f.id)}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-mono font-medium border transition-all duration-150',
                activeFamily === f.id
                  ? 'bg-amber/10 text-amber border-amber/25 shadow-amber/5'
                  : 'bg-bg-elevated text-txt-secondary border-bg-border hover:text-txt-primary hover:border-bg-hover'
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Template Grid */}
        {filtered.length === 0 ? (
          <EmptyState
            title="No templates in this category"
            description="Select a different filter or start with Custom Multi-Leg"
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.map((template) => (
              <StrategyCard
                key={template.id}
                template={template}
                onUse={handleUseTemplate}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Recent / Saved ── */}
      <div className="panel p-5 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock size={13} className="text-txt-muted" />
            <h2 className="font-display font-semibold text-sm text-txt-primary tracking-wide">
              Recent Strategies
            </h2>
            {!strategiesLoading && strategies.length > 0 && (
              <span className="badge bg-bg-elevated text-txt-muted border border-bg-border text-2xs font-mono">
                {strategies.length}
              </span>
            )}
          </div>
        </div>

        {strategiesLoading ? (
          <div className="text-2xs font-mono text-txt-muted">Loading…</div>
        ) : strategies.length === 0 ? (
          <EmptyState
            title="No saved strategies yet"
            description="Use a template or start a new strategy to see it here"
            action={
              <button
                onClick={handleNewStrategy}
                className="btn-secondary text-xs flex items-center gap-1.5 mt-1"
              >
                <Plus size={12} />
                New Strategy
              </button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {strategies.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => navigate(ROUTES.options(s.id), { state: { strategy: s } })}
                className="panel p-3 flex flex-col gap-1.5 text-left hover:border-bg-hover transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-display font-semibold text-sm text-txt-primary truncate">
                    {s.name}
                  </span>
                  {s.family && (
                    <span className={cn(
                      'badge border text-2xs font-mono shrink-0',
                      s.family === 'bullish'    ? 'bg-bull/10 text-bull border-bull/20' :
                      s.family === 'bearish'    ? 'bg-bear/10 text-bear border-bear/20' :
                      s.family === 'neutral'    ? 'bg-info/10 text-info border-info/20' :
                      s.family === 'volatility' ? 'bg-amber/10 text-amber border-amber/20' :
                                                  'bg-bg-elevated text-txt-muted border-bg-border'
                    )}>
                      {s.family}
                    </span>
                  )}
                </div>
                <span className="text-2xs font-mono text-txt-secondary">
                  {s.underlying} · {s.expiry} · lot {s.lotSize}
                </span>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <span className="text-2xs font-mono text-txt-muted">
                    {String(s.createdAt || '—').slice(0, 10)}
                  </span>
                  <span className={cn(
                    'badge border text-2xs font-mono',
                    s.status === 'deployed' ? 'bg-bull/10 text-bull border-bull/20' :
                    s.status === 'saved'    ? 'bg-amber/10 text-amber border-amber/20' :
                                              'bg-bg-elevated text-txt-muted border-bg-border'
                  )}>
                    {s.status || 'draft'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
